import { Controller } from "@hotwired/stimulus"

// Reusable ASX ticker autocomplete for a form field.
//
// SHARED — two consumers today, on different gates:
//
//   * /ai (Pro)                  — app/views/ai/_ticker_search_form.html.slim
//   * portfolio trade entry      — app/views/portfolios/trades/_fields.html.slim
//
// So a change here touches a live paid feature, not just the newest one. Run
// BOTH spec/system/ai_ticker_search_spec.rb and
// spec/system/portfolio_trade_entry_spec.rb, plus this controller's jest file.
// /ai carried its own copy of all of this until 2026-08-26; the two had already
// drifted on accessibility and on how they escaped the company name, which is
// why there is now one.
//
// Consumers react to a pick via the `ticker-autocomplete:selected` event
// (detail: { ticker, name }, bubbles) rather than by this controller knowing
// anything about them — see ai_chart_controller#tickerSelected.
//
// Contract the views use:
//
//   data-controller="ticker-autocomplete"
//   data-ticker-autocomplete-url-value="/portfolios/1/trades/ticker_search"
//
//   targets: input (the text field)
//            results (empty container the dropdown is rendered into)
//            hint (OPTIONAL — shows the matched company name; may be absent)
//
//   actions: input->ticker-autocomplete#search
//            focus->ticker-autocomplete#reopen
//            keydown->ticker-autocomplete#navigate
//            blur->ticker-autocomplete#normalize
//
// The endpoint returns [{ ticker, name }, ...].
//
// Everything is built with createElement/textContent: the CSP is ENFORCED in
// production, and interpolating a server-supplied company name into innerHTML
// would be both an injection path and one bad character away from broken markup.
export default class extends Controller {
  static targets = ["input", "results", "hint"]
  static values = {
    url: String,
    debounce: { type: Number, default: 200 },
    minLength: { type: Number, default: 1 }
  }

  connect() {
    this.searchTimeout = null
    this.abortController = null
    this.selectedIndex = -1
    this.lastQuery = null
    this.lastResults = []
    // The field may arrive pre-filled server-side (the /ai page renders the
    // selected ticker), which is a settled selection, not a pending query.
    this.selectedTicker = this.query() || null
    // Option ids must be unique per controller instance — two of these on one
    // page (a form and a filter, say) would otherwise both claim #tac-option-0
    // and aria-activedescendant would point at the wrong list.
    this.instanceId = `tac-${Math.random().toString(36).slice(2, 9)}`

    // Close on any click elsewhere. Without this the dropdown sits open over
    // the rest of the form and swallows clicks, which reads as a frozen page.
    this.onDocumentPointerDown = (event) => {
      if (this.hasInputTarget && this.inputTarget.contains(event.target)) return
      if (this.hasResultsTarget && this.resultsTarget.contains(event.target)) return
      this.close()
    }
    document.addEventListener("pointerdown", this.onDocumentPointerDown)
  }

  disconnect() {
    // A pending timer, an in-flight fetch and a document listener all outlive
    // the element unless they're torn down here; this app has shipped real bugs
    // from each of the three.
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
    this.abortInFlight()
    if (this.onDocumentPointerDown) {
      document.removeEventListener("pointerdown", this.onDocumentPointerDown)
      this.onDocumentPointerDown = null
    }
  }

  abortInFlight() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  // --- actions -------------------------------------------------------------

  search() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }

    const query = this.query()

    // Typing invalidates any previous pick — leaving the old company name under
    // a half-typed ticker states a match that no longer exists, and the field
    // is a query again rather than a settled selection.
    this.clearHint()
    this.selectedTicker = null

    if (query.length < this.minLengthValue) {
      this.abortInFlight()
      this.lastQuery = null
      this.lastResults = []
      this.close()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.searchTimeout = null
      this.performSearch(query)
    }, this.debounceValue)
  }

  // Returning to the field should show what we already have. Escape (or a click
  // away) previously left the user with no way back short of editing the text,
  // which looks like the search has died.
  reopen() {
    const query = this.query()
    if (query.length < this.minLengthValue) return

    // A field still holding exactly what the user picked is settled, not a
    // query awaiting an answer. Without this, tabbing back into the box drops
    // the list over whatever sits below it — on /ai that is the chart the pick
    // just loaded, which reads as the selection having been undone.
    if (query === this.selectedTicker) return

    if (query === this.lastQuery && this.lastResults.length > 0) {
      this.render(this.lastResults)
    } else {
      this.search()
    }
  }

  navigate(event) {
    const options = this.options()

    if (event.key === "Escape") {
      // Escape closes only — the typed text is the user's, not ours to revert.
      this.close()
      return
    }

    if (options.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1)
      this.highlight(options)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.highlight(options)
    } else if (event.key === "Enter") {
      // Always swallow Enter while the dropdown is open: the keystroke means
      // "take this suggestion", and letting it through submits a half-filled
      // trade form instead.
      event.preventDefault()
      const option = this.enterSelection(options)
      if (option) option.click()
    }
  }

  // The server resolves the ticker by exact match, and people type "bhp".
  normalize() {
    if (!this.hasInputTarget) return
    this.inputTarget.value = this.inputTarget.value.trim().toUpperCase()
  }

  // --- search --------------------------------------------------------------

  async performSearch(query) {
    // A newer keystroke supersedes anything still in flight — otherwise a slow
    // early response lands last and overwrites the results for what the user
    // actually typed.
    this.abortInFlight()

    const controller = new AbortController()
    this.abortController = controller

    try {
      const response = await fetch(this.searchUrl(query), {
        signal: controller.signal,
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) throw new Error(`Ticker search failed (${response.status})`)

      const results = await response.json()

      if (this.stale(controller, query)) return

      this.lastQuery = query
      this.lastResults = Array.isArray(results) ? results : []
      this.render(this.lastResults)
    } catch (error) {
      // An abort is the expected outcome of typing fast, not an error worth
      // reporting; anything else clears the stale list (and stays out of the
      // console) but SAYS so — silently closing leaves the user retyping a
      // code that was fine, against a search that is down.
      if (error.name === "AbortError") return
      if (this.stale(controller, query)) return

      this.lastQuery = null
      this.lastResults = []
      this.renderMessage("Search is unavailable right now.")
    } finally {
      if (this.abortController === controller) this.abortController = null
    }
  }

  // True when this response no longer describes what is in the field, either
  // because a newer request started or because the user kept typing.
  stale(controller, query) {
    if (this.abortController && this.abortController !== controller) return true
    if (!this.hasInputTarget) return true
    return this.query() !== query
  }

  searchUrl(query) {
    const base = this.urlValue || ""
    const separator = base.includes("?") ? "&" : "?"
    return `${base}${separator}q=${encodeURIComponent(query)}`
  }

  // Upper-cased here, not only on blur. `normalize()` fires on blur, and a
  // blur can land BETWEEN a keystroke and its debounced response — clicking a
  // result blurs the input first — so a query captured as "bhp" would come
  // back to a field reading "BHP" and be discarded as stale by `stale()`. One
  // canonical form for the whole controller removes the race; the field is
  // styled text-uppercase and the server matches on the upper-cased code, so
  // this is also what the user sees and what the endpoint wants.
  query() {
    return this.hasInputTarget ? this.inputTarget.value.trim().toUpperCase() : ""
  }

  // --- rendering -----------------------------------------------------------

  render(results) {
    if (!this.hasResultsTarget) return

    // "No matches" is an ANSWER, not an absence. A dropdown that simply
    // vanishes when the search comes back empty is indistinguishable from one
    // that is still loading or has broken, and the user retypes the same code
    // waiting for something to happen.
    if (!results || results.length === 0) {
      this.renderMessage("No matching securities.")
      return
    }

    const container = this.resultsTarget
    container.replaceChildren()

    const list = document.createElement("div")
    list.className = "list-group"
    list.setAttribute("role", "listbox")

    results.forEach((result, index) => {
      // A button, not an anchor: inside a form an <a> is a stray link, and a
      // default-typed button submits the form on click.
      const option = document.createElement("button")
      option.type = "button"
      option.className = "list-group-item list-group-item-action"
      option.id = `${this.instanceId}-option-${index}`
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", "false")
      option.dataset.ticker = result.ticker || ""

      const ticker = document.createElement("strong")
      ticker.textContent = result.ticker || ""
      option.appendChild(ticker)

      const name = document.createElement("span")
      name.className = "text-muted small ms-2"
      name.textContent = result.name || ""
      option.appendChild(name)

      // addEventListener, never an inline onclick — inline handlers are blocked
      // outright by the enforced script-src-attr policy in production.
      option.addEventListener("click", () => this.select(result.ticker, result.name))

      list.appendChild(option)
    })

    container.appendChild(list)
    container.classList.remove("d-none")
    this.setExpanded(true)
    this.selectedIndex = -1
    this.setActiveDescendant(null)
  }

  // A non-selectable status row, so the dropdown always explains itself. Kept
  // out of the options list deliberately: it carries no role="option" and no
  // click handler, so Arrow/Enter cannot land on it and "select" a message.
  renderMessage(message) {
    if (!this.hasResultsTarget) return

    const container = this.resultsTarget
    container.replaceChildren()

    const list = document.createElement("div")
    list.className = "list-group"

    const row = document.createElement("div")
    row.className = "list-group-item disabled text-muted small"
    row.textContent = message
    list.appendChild(row)

    container.appendChild(list)
    container.classList.remove("d-none")
    this.setExpanded(true)
    this.selectedIndex = -1
    this.setActiveDescendant(null)
  }

  close() {
    if (this.hasResultsTarget) {
      this.resultsTarget.replaceChildren()
      this.resultsTarget.classList.add("d-none")
    }
    this.selectedIndex = -1
    this.setExpanded(false)
    this.setActiveDescendant(null)
  }

  options() {
    if (!this.hasResultsTarget) return []
    return Array.from(this.resultsTarget.querySelectorAll('button[role="option"]'))
  }

  highlight(options) {
    options.forEach((option, index) => {
      const active = index === this.selectedIndex
      option.classList.toggle("active", active)
      option.setAttribute("aria-selected", active ? "true" : "false")
    })
    const current = options[this.selectedIndex]
    this.setActiveDescendant(current ? current.id : null)
  }

  // Which row Enter picks: the highlighted one, the only one, or an exact
  // ticker match for what has been typed.
  enterSelection(options) {
    if (this.selectedIndex >= 0 && this.selectedIndex < options.length) {
      return options[this.selectedIndex]
    }
    if (options.length === 1) return options[0]

    const typed = this.query()
    return options.find(option => option.dataset.ticker === typed) || null
  }

  select(ticker, name) {
    const upper = (ticker || "").toUpperCase()

    if (this.hasInputTarget) this.inputTarget.value = upper
    // Treat the pick as the current query so refocusing the field doesn't
    // immediately re-open the dropdown over the rest of the form.
    this.lastQuery = upper
    this.selectedTicker = upper
    this.setHint(name)
    this.close()

    // Fired on the controller element so a consumer (price prefill, position
    // lookup) can react without this controller knowing anything about it.
    this.element.dispatchEvent(new CustomEvent("ticker-autocomplete:selected", {
      detail: { ticker: upper, name: name || "" },
      bubbles: true
    }))
  }

  // --- optional hint -------------------------------------------------------

  setHint(name) {
    if (!this.hasHintTarget) return
    this.hintTarget.textContent = name || ""
  }

  clearHint() {
    this.setHint("")
  }

  setExpanded(expanded) {
    if (!this.hasInputTarget) return
    this.inputTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  setActiveDescendant(id) {
    if (!this.hasInputTarget) return
    if (id) {
      this.inputTarget.setAttribute("aria-activedescendant", id)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }
}
